import { useMemo } from 'react';
import { IDepositExtended } from 'views/types/typeTransaction';
import { formatAmount, isDebitAmount } from 'utils/amountUtils';
import { formatUnits } from 'viem';
import { EOrderType } from 'views/types/transactions';

// Hook for transaction type logic
export const useTransactionType = (transaction: IDepositExtended) => {
  return useMemo(() => {
    const type = transaction?.type;

    if (!type) return '---';

    if (type.toLowerCase().includes('internal')) {
      const isPositive = +transaction.amountIn > 0;

      if (isPositive) {
        return 'Internal Deposit';
      } else {
        return 'Internal Withdrawal';
      }
    }

    return type.replaceAll('-', ' ');
  }, [transaction]);
};

// Hook for network information
export const useNetworkInfo = (transaction: IDepositExtended) => {
  return useMemo(() => {
    if (transaction?.type?.includes('card')) {
      return 'Internal';
    }

    if (
      transaction?.contractIn?.network?.name ||
      transaction?.contractOut?.network?.name
    ) {
      return (
        transaction?.contractIn?.network?.name ||
        transaction?.contractOut?.network?.name
      );
    }

    if (
      transaction?.transactionHash ||
      transaction?.transactionInHash ||
      transaction?.transactionOutHash
    ) {
      return 'External Network';
    }

    return 'Internal';
  }, [
    transaction?.type,
    transaction?.contractIn?.network?.name,
    transaction?.contractOut?.network?.name,
    transaction?.transactionHash,
    transaction?.transactionInHash,
    transaction?.transactionOutHash,
  ]);
};

// Hook for debit/credit calculation
export const useDebitCreditInfo = (transaction: IDepositExtended) => {
  return useMemo(() => {
    const amount = transaction?.amountIn ?? transaction?.amount;
    const currency = transaction?.currencyIn || transaction?.currency;
    const currencySymbol = currency?.symbol || '---';
    const network = transaction?.contractIn?.network.name || '---';

    const formattedAmount = formatAmount(
      +formatUnits(BigInt(amount), currency?.decimals || 18)
    );
    const type =
      isDebitAmount(amount) ||
      [
        EOrderType.CARD_ISSUE,
        EOrderType.AML_TX_CHECK,
        EOrderType.AML_WALLET_CHECK,
      ].includes(transaction.type as EOrderType)
        ? 'Debit'
        : 'Credit';

    return {
      type,
      amount: formattedAmount,
      currency: currencySymbol,
      network,
    };
  }, [
    transaction?.amountIn,
    transaction?.amount,
    transaction?.currencyIn,
    transaction?.currency,
    transaction?.contractIn?.network.name,
    transaction.type,
  ]);
};

// Hook for wallet/user information
export const useWalletInfo = (
  transaction: IDepositExtended,
  isInternal: boolean
) => {
  return useMemo(() => {
    if (isInternal) {
      return {
        from:
          transaction?.sender?.username ||
          transaction?.sender?.coinsFlyId ||
          transaction?.user?.username ||
          transaction?.user?.coins_fly_id ||
          '---',
        to:
          transaction?.receiver?.username ||
          transaction?.receiver?.coinsFlyId ||
          '---',
      };
    }

    return {
      from: transaction?.walletSender || '---',
      to: transaction?.walletReceiver || '---',
    };
  }, [
    isInternal,
    transaction?.sender,
    transaction?.receiver,
    transaction?.user,
    transaction?.walletSender,
    transaction?.walletReceiver,
  ]);
};

// Hook for fee breakdown
export const useFeeInfo = (transaction: IDepositExtended) => {
  return useMemo(() => {
    const fees = [];
    const currency =
      transaction.type === 'swap'
        ? transaction?.currencyOut || transaction?.currency
        : transaction?.currencyIn || transaction?.currency;
    const currencySymbol = currency?.symbol || '---';

    if (transaction?.fee) {
      const decimals = currency?.decimals;

      const feeInSmallestUnits = BigInt(
        Math.round(+transaction.fee * 10 ** (decimals || 6))
      );

      fees.push({
        type: 'Network Fee',
        amount: formatUnits(feeInSmallestUnits, decimals || 6),
        currency: currencySymbol,
      });
    }

    if (transaction?.amlFee) {
      fees.push({
        type: 'AML Fee',
        amount: formatAmount(transaction.amlFee),
        currency: currencySymbol,
      });
    }

    return fees;
  }, [
    transaction?.fee,
    transaction?.amlFee,
    transaction?.currency,
    transaction?.currencyIn,
    transaction?.currencyOut,
    transaction?.type,
  ]);
};

// Combined hook for all transaction display data
export const useTransactionDisplayData = (
  transaction: IDepositExtended,
  isInternal: boolean
) => {
  const transactionType = useTransactionType(transaction);
  const networkInfo = useNetworkInfo(transaction);
  const debitCreditInfo = useDebitCreditInfo(transaction);
  const walletInfo = useWalletInfo(transaction, isInternal);
  const feeInfo = useFeeInfo(transaction);

  return {
    transactionType,
    networkInfo,
    debitCreditInfo,
    walletInfo,
    feeInfo,
  };
};
